import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Divider, Form, Select } from "antd";
import NGIInput from "components/Common/inputs/NGIInput";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import NGISelect from "components/Common/inputs/NGISelect";
import { USER_CURRENCY, USER_ROLES } from "utils/constants";
import CustomButton from "components/Common/buttons/CustomButton";
import { updateBundle } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import moment from "moment/moment";
import { TYPES } from "pages/price-management/Bundles/variables";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import { useHistory } from "react-router";

const { Option } = Select;

const useStyles = makeStyles({
  root: {
    padding: "30px 40px",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    flex: "1!important",
    margin: "0px!important",
  },
  formRow: {
    display: "flex",
    padding: "10px 0",
    gap: 15,
  },
  fieldsContainer: {},
  rowTitle: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  subTitle: {
    fontSize: 11,
    fontWeight: 300,
    color: "#000000BF",
  },
  liteSelect: {
    "&.ant-select:not(.ant-select-customize-input) .ant-select-selector": {
      border: "2px solid #0000001A",
      borderRadius: 5,
    },
  },
  liteInput: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  currency: {
    backgroundColor: "#0000000D",
    borderRadius: 2,
    fontSize: 9,
    color: "#666666",
    padding: "2px 8px",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontSize: 16,

    "&>div": {
      fontSize: 11,
      fontWeight: 300,
      color: "#8C8C8C",
    },
  },
});

const EditBundleTab = ({ data, hasAssignments, setIsLoading }) => {
  const classes = useStyles();
  const history = useHistory();
  const [form] = Form.useForm();
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [type, setType] = useState(null);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        activationDate: moment(data.activationDate),
        deactivationDate: data.deactivationDate ? moment(data.deactivationDate) : null,
        cookingTime: data.lpgOrTime,
        gasQuantity: data.lpgOrTime,
      });
      setType(data.type);
    }
  }, [data]);

  const onSubmit = useCallback(
    values => {
      const {
        type,
        name,
        displayName,
        validityPeriod,
        price,
        activationDate,
        deactivationDate,
        gasQuantity,
        cookingTime,
        gasLimit,
      } = values;

      setIsLoading(true);
      const preparedData =
        data.status === "Active"
          ? {
              deactivationDate: customDateFormatWithoutTime(deactivationDate),
            }
          : {
              name,
              displayName,
              type,
              validityPeriod: Number(validityPeriod),
              price: Number(price),
              activationDate: customDateFormatWithoutTime(activationDate),
              ...(deactivationDate && {
                deactivationDate: customDateFormatWithoutTime(deactivationDate),
              }),
              ...(type === "KB"
                ? {
                    gasQuantity: Number(gasQuantity),
                  }
                : { cookingTime: Number(cookingTime), gasLimit: Number(gasLimit) }),
            };
      updateBundle(data.id, preparedData)
        .then(() => {
          setTimeout(() => {
            setIsLoading(false);
            successMessage("Gas bundle was updated successfully");
            history.push("/price-management/bundles");
          }, 500);
        })
        .catch(error => {
          setIsLoading(false);
          defaultNgiErrorMethod(error);
        });
    },
    [data, form]
  );

  const disabled = useMemo(
    () => ["Inactive", "Active", hasAssignments && "New"].includes(data?.status),
    [data, hasAssignments]
  );

  const openConfirmationModal = useCallback(e => {
    e?.preventDefault();
    setIsConfirmationVisible(true);
  }, []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationVisible(false), []);

  return (
    <div className={classes.root}>
      <Form form={form} onFinish={onSubmit} name="updateBundle" layout="inline" className={classes.formContainer}>
        <div className={classes.actions}>
          <div className={classes.headerContainer}>
            General Settings
            <div>Set up all bundle settings and rules before assigning it to customers</div>
          </div>
          {data?.status !== "Inactive" && (
            <CustomButton
              hideRoles={[USER_ROLES.FINANCE]}
              onClick={openConfirmationModal}
              size="lite"
              text="Save Changes"
              type="primary"
            />
          )}
        </div>
        <Divider />

        <div className={classes.fieldsContainer}>
          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Bundle Name</div>
              <div className={classes.subTitle}>Give unique name</div>
            </div>
            <Form.Item
              className={classes.formItem}
              name="name"
              rules={[{ required: true, message: "Please enter value" }]}
            >
              <NGIInput disabled={disabled} placeholder="Enter Name" className={classes.liteInput} />
            </Form.Item>
          </div>
          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Display Name </div>
              <div className={classes.subTitle}>Name displayed on customer’s meter</div>
            </div>
            <Form.Item
              className={classes.formItem}
              name="displayName"
              rules={[{ required: true, message: "Please enter value" }]}
            >
              <NGIInput disabled={disabled} placeholder="Enter Display Name" className={classes.liteInput} />
            </Form.Item>
          </div>
          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Bundle Type</div>
              <div className={classes.subTitle}>Select between consumption or time based bundles</div>
            </div>
            <Form.Item
              className={classes.formItem}
              name="type"
              rules={[{ required: true, message: "Please enter value" }]}
            >
              <NGISelect
                disabled
                onChange={setType}
                showSearch
                className={classes.liteSelect}
                placeholder="Select Type"
              >
                {TYPES.map(item => (
                  <Option key={`${item.value}-option`} value={item.value}>
                    {item.name}
                  </Option>
                ))}
              </NGISelect>
            </Form.Item>
          </div>

          <Divider />

          {type === "TB" ? (
            <>
              <div className={classes.formRow}>
                <div className={classes.rowTitle}>
                  <div className={classes.title}>Cooking Time</div>
                  <div className={classes.subTitle}>Set the cooking time in the bundle</div>
                </div>
                <Form.Item
                  className={classes.formItem}
                  name="cookingTime"
                  rules={[
                    { required: true, message: "Please enter value" },
                    {
                      message: "Should be a number. Allowed 2 digits after comma",
                      pattern: /^\d+(\.\d{1,2})?$/,
                    },
                  ]}
                >
                  <NGIInput
                    disabled={disabled}
                    placeholder="Enter Cooking Time"
                    className={classes.liteInput}
                    suffix={<span className={classes.currency}>MIN</span>}
                  />
                </Form.Item>
              </div>
              <div className={classes.formRow}>
                <div className={classes.rowTitle}>
                  <div className={classes.title}>Gas Limit</div>
                  <div className={classes.subTitle}>Set the upper control gas limit</div>
                </div>
                <Form.Item
                  className={classes.formItem}
                  name="gasLimit"
                  rules={[
                    { required: true, message: "Please enter value" },
                    {
                      message: "Should be a number. Allowed 2 digits after comma",
                      pattern: /^\d+(\.\d{1,2})?$/,
                    },
                  ]}
                >
                  <NGIInput
                    disabled={disabled}
                    placeholder="Enter Gas Limit"
                    className={classes.liteInput}
                    suffix={<span className={classes.currency}>KG</span>}
                  />
                </Form.Item>
              </div>
            </>
          ) : (
            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Gas in Bundle</div>
                <div className={classes.subTitle}>Set the quantity of gas in the bundle</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="gasQuantity"
                rules={[
                  { required: true, message: "Please enter value" },
                  {
                    message: "Should be a number. Allowed 2 digits after comma",
                    pattern: /^\d+(\.\d{1,2})?$/,
                  },
                ]}
              >
                <NGIInput
                  disabled={disabled}
                  placeholder="Enter value"
                  className={classes.liteInput}
                  suffix={<span className={classes.currency}>KG</span>}
                />
              </Form.Item>
            </div>
          )}

          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Bundle Price</div>
              <div className={classes.subTitle}>Set the total bundle price (for all KG’s of gas)</div>
            </div>
            <Form.Item
              className={classes.formItem}
              name="price"
              rules={[
                { required: true, message: "Please enter value" },
                {
                  message: "Should be a number. Allowed 2 digits after comma",
                  pattern: /^\d+(\.\d{1,2})?$/,
                },
              ]}
            >
              <NGIInput
                disabled={disabled}
                placeholder="Enter price"
                className={classes.liteInput}
                suffix={<span className={classes.currency}>{USER_CURRENCY()}</span>}
              />
            </Form.Item>
          </div>
          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Bundle Duration Period</div>
              <div className={classes.subTitle}>Set the time (in days) the bundle will be active after activation</div>
            </div>
            <Form.Item
              className={classes.formItem}
              name="validityPeriod"
              rules={[
                { required: true, message: "Please enter value" },
                {
                  message: "Should be a integer",
                  pattern: /^\d+$/,
                },
              ]}
            >
              <NGIInput
                disabled={disabled}
                placeholder="Enter Duration Period"
                className={classes.liteInput}
                suffix={<span className={classes.currency}>DAYS</span>}
              />
            </Form.Item>
          </div>

          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Bundle Start Date</div>
              <div className={classes.subTitle}>Set the first day this bundle will be available for activation</div>
            </div>
            <Form.Item
              className={classes.formItem}
              name="activationDate"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <NGIDatePicker
                onChange={() => {
                  form.setFieldValue("deactivationDate", null);
                }}
                disabled={disabled}
                disableDates={current => moment() >= current}
                className={classes.liteInput}
                placeholder="Bundle Start Date"
              />
            </Form.Item>
          </div>
          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Bundle End Date</div>
              <div className={classes.subTitle}>Set the last day this bundle will be available for activation</div>
            </div>
            <Form.Item className={classes.formItem} name="deactivationDate">
              <NGIDatePicker
                allowClear
                disabled={data?.status === "Inactive"}
                disableDates={current =>
                  form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) > current
                }
                className={classes.liteInput}
                placeholder="Bundle End Date"
              />
            </Form.Item>
          </div>
        </div>
      </Form>

      {isConfirmationVisible && (
        <ConfirmationModal
          onCancel={closeConfirmationModal}
          onConfirm={form.submit}
          description="Are you sure you want to save changes?"
        />
      )}
    </div>
  );
};

export default EditBundleTab;
